import {browserGetBrowserWidth,
        browserAfterRendering,
        BROWSER_SMALL_SCREEN,
        browserIsSmallScreen,
        browserGetCookie
        } from "./browser.js";
import { cacheGetCacheTime } from "./cache.js";
import { pagePxSizeToInt } from "./page.js";
import { POSTER_MINIMUM_WIDTH } from "./poster.js";
import { i18nGetPageLanguage, i18nText } from "./i18n.js";
import CONFIG from "./../config.json";

const POSTERS_BIG_SCREENS_MARGIN = 66; // 16*2 + 16*2 + 1*2
const POSTERS_SMALL_SCREENS_MARGIN = 36; // 16*2 + 1*2 + 1*2
const POSTERS_SCREEN_BOUNDARY = 640;
const POSTERS_FETCH_NUMBER = 20;
const POSTERS_IMAGE_MAXIMUM_SIZE = 512;
const POSTERS_IMAGE_FALLBACK_ASPECT_RATIO = "4 / 5";

const _posters = []; // caches, cursors could imply posters repetitions
let _postersIndex = 0;
let _postersAbsoluteIndex = 0;
let _postersToReturn = 0;
let _postersElementWrapper;
const _postersToResolve = [];
let _postersBatchNumber = 0;
let _postersAllDownloaded = false;
let _postersFetching = false;
let _postersReject;
let _postersFilter = "";
let _postersFilterTown = "";
let _postersFilterRegion = "";
let _postersFilterPublisherId = "";
const _postersCursor = [];
let _postersIndexMapKey = null;
let _postersIndexMap = {};
let _postersPosterIndex = {};
let _postersOthersIndexMapKey = null;
let _postersOthersIndexMap = {};
let _postersOthersPosterIndex = {};
let _postersIndexMode = "main";
let _postersOthersBaseIndex = 0;
let _postersExcludedIds = null;

function postersGetStorageCacheSuffix() {
    const cacheTime = cacheGetCacheTime();
    if (cacheTime !== null && cacheTime !== "") {
        return cacheTime;
    }
    const cookieCacheTime = browserGetCookie("cacheTime");
    if (cookieCacheTime) {
        return cookieCacheTime;
    }
    return "default";
}

function postersBuildIndexKey(prefix) {
    if (typeof window === "undefined") {
        return null;
    }
    const url = new URL(window.location.href);
    const params = new URLSearchParams(url.search);
    params.delete("index");
    params.delete("otherIndex");
    params.delete("cursor");
    params.delete("v");
    const query = params.toString();
    return (
        prefix +
        postersGetStorageCacheSuffix() +
        ":" +
        url.pathname +
        (query ? "?" + query : "")
    );
}

function postersClearStaleIndexMaps(activeMainKey, activeOthersKey) {
    if (typeof window === "undefined") {
        return;
    }
    try {
        for (let i = sessionStorage.length - 1; i >= 0; i--) {
            const key = sessionStorage.key(i);
            if (!key) {
                continue;
            }
            const isMainKey = key.startsWith("postersIndexMap:");
            const isOthersKey = key.startsWith("postersOthersIndexMap:");
            if ((isMainKey || isOthersKey) &&
                    key !== activeMainKey &&
                    key !== activeOthersKey) {
                sessionStorage.removeItem(key);
            }
        }
    } catch (err) {
        // Ignore storage failures.
    }
}

function postersLoadIndexMaps() {
    const nextPostersIndexMapKey = postersBuildIndexKey("postersIndexMap:");
    const nextPostersOthersIndexMapKey = postersBuildIndexKey("postersOthersIndexMap:");
    const keysChanged =
        _postersIndexMapKey !== nextPostersIndexMapKey ||
        _postersOthersIndexMapKey !== nextPostersOthersIndexMapKey;

    if (keysChanged) {
        _postersIndexMap = {};
        _postersPosterIndex = {};
        _postersOthersIndexMap = {};
        _postersOthersPosterIndex = {};
        _postersIndexMapKey = nextPostersIndexMapKey;
        _postersOthersIndexMapKey = nextPostersOthersIndexMapKey;
        postersClearStaleIndexMaps(_postersIndexMapKey, _postersOthersIndexMapKey);
    }
    try {
        if (_postersIndexMapKey) {
            const raw = sessionStorage.getItem(_postersIndexMapKey);
            if (raw) {
                const parsed = JSON.parse(raw);
                if (parsed && typeof parsed === "object") {
                    _postersIndexMap = parsed;
                }
            }
            _postersPosterIndex = {};
            for (const key in _postersIndexMap) {
                const index = parseInt(key, 10);
                const posterId = _postersIndexMap[key];
                if (Number.isNaN(index) || !posterId) {
                    continue;
                }
                if (
                    _postersPosterIndex[posterId] === undefined ||
                    index < _postersPosterIndex[posterId]
                ) {
                    _postersPosterIndex[posterId] = index;
                }
            }
        }
        if (_postersOthersIndexMapKey) {
            const rawOthers = sessionStorage.getItem(_postersOthersIndexMapKey);
            if (rawOthers) {
                const parsedOthers = JSON.parse(rawOthers);
                if (parsedOthers && typeof parsedOthers === "object") {
                    _postersOthersIndexMap = parsedOthers;
                }
            }
            _postersOthersPosterIndex = {};
            for (const key in _postersOthersIndexMap) {
                const index = parseInt(key, 10);
                const posterId = _postersOthersIndexMap[key];
                if (Number.isNaN(index) || !posterId) {
                    continue;
                }
                if (
                    _postersOthersPosterIndex[posterId] === undefined ||
                    index < _postersOthersPosterIndex[posterId]
                ) {
                    _postersOthersPosterIndex[posterId] = index;
                }
            }
        }
    } catch (err) {
        // Ignore storage failures.
    }
}

function postersSaveIndexMaps() {
    try {
        if (_postersIndexMapKey) {
            sessionStorage.setItem(
                _postersIndexMapKey,
                JSON.stringify(_postersIndexMap)
            );
        }
        if (_postersOthersIndexMapKey) {
            sessionStorage.setItem(
                _postersOthersIndexMapKey,
                JSON.stringify(_postersOthersIndexMap)
            );
        }
    } catch (err) {
        // Ignore storage failures.
    }
}

function postersGetMainMaxIndex() {
    let max = -1;
    for (const key in _postersIndexMap) {
        const index = parseInt(key, 10);
        if (!Number.isNaN(index) && index > max) {
            max = index;
        }
    }
    return max;
}

function postersSetIndexMode(mode) {
    _postersIndexMode = mode === "others" ? "others" : "main";
    postersLoadIndexMaps();
    if (_postersIndexMode === "others") {
        _postersOthersBaseIndex = 0;
    }
}

postersLoadIndexMaps();

function postersReset() {
    _posters.length = 0;
    _postersIndex = 0;
    _postersAbsoluteIndex = 0;
    _postersToReturn = 0;
    _postersToResolve.length = 0;
    _postersBatchNumber = 0;
    _postersAllDownloaded = false;
    _postersFetching = false;
    _postersFilter = "";
    _postersFilterTown = "";
    _postersFilterRegion = "";
    _postersFilterPublisherId = "";
    _postersCursor.length = 0;
    _postersExcludedIds = null;
    postersLoadIndexMaps();
}

function postersResetResolve() {
    _postersToReturn = 0;
    _postersToResolve.length = 0;
}

function postersGetMargin() {
    if (browserGetBrowserWidth() <= POSTERS_SCREEN_BOUNDARY) {
        return POSTERS_SMALL_SCREENS_MARGIN;
    } else {
        return POSTERS_BIG_SCREENS_MARGIN;
    }
}

function getPostersMaximumImageWidth(naturalWidth, naturalHeight) {
    if ((naturalWidth > POSTERS_IMAGE_MAXIMUM_SIZE) && (naturalWidth >= naturalHeight)) {
        return POSTERS_IMAGE_MAXIMUM_SIZE;
    } else if (naturalHeight > POSTERS_IMAGE_MAXIMUM_SIZE) {
        return (POSTERS_IMAGE_MAXIMUM_SIZE * naturalWidth) / naturalHeight;
    }
    return naturalWidth;
}

function postersGetAspectRatioStorageKey(poster) {
    if (!poster) {
        return "";
    }

    const posterId = poster.getAttribute("data-post-id");
    if (!posterId) {
        return "";
    }

    return "posterAspectRatio:" + posterId;
}

function postersGetStoredAspectRatio(poster) {
    if (typeof window === "undefined") {
        return "";
    }

    const key = postersGetAspectRatioStorageKey(poster);
    if (key === "") {
        return "";
    }

    try {
        const stored = sessionStorage.getItem(key);
        return typeof stored === "string" ? stored : "";
    } catch (err) {
        return "";
    }
}

function postersStoreAspectRatio(poster, naturalWidth, naturalHeight) {
    if (typeof window === "undefined") {
        return;
    }

    if (naturalWidth <= 0 || naturalHeight <= 0) {
        return;
    }

    const key = postersGetAspectRatioStorageKey(poster);
    if (key === "") {
        return;
    }

    try {
        sessionStorage.setItem(key, naturalWidth + " / " + naturalHeight);
    } catch (err) {
        // Ignore storage failures.
    }
}

function postersGetImageWrapper(poster) {
    if (!poster) {
        return null;
    }

    const wrappers = poster.getElementsByClassName("posterImageWrapper");
    if (wrappers.length === 0) {
        return null;
    }

    return wrappers[0];
}

function postersApplyPosterImageAspectRatio(poster, naturalWidth = 0, naturalHeight = 0) {
    const imageWrapper = postersGetImageWrapper(poster);
    if (!imageWrapper) {
        return;
    }

    let ratio = "";
    if (naturalWidth > 0 && naturalHeight > 0) {
        ratio = naturalWidth + " / " + naturalHeight;
        postersStoreAspectRatio(poster, naturalWidth, naturalHeight);
    } else {
        ratio = postersGetStoredAspectRatio(poster);
    }

    if (
        ratio === "" &&
        poster &&
        poster.id === "poster-main-poster"
    ) {
        // Main poster: avoid forcing fallback ratio before real image dimensions exist.
        imageWrapper.style.aspectRatio = "auto";
        return;
    }

    if (ratio === "") {
        ratio = POSTERS_IMAGE_FALLBACK_ASPECT_RATIO;
    }

    imageWrapper.style.aspectRatio = ratio;
}

function postersSetPosterResposiveWidth(
        poster,
        width,
        margin,
        checkTitleOverflow = true
        ) {

    if ((poster.id) && (poster.id === "poster-main-poster"))
    {
        if (browserIsSmallScreen() === false)
            return;
    }

    poster.style.width = postersGetPosterResposiveWidth(width, margin) + "px";

    // poster title is overflowing after rendering?
    if (checkTitleOverflow === true) {
        postersCheckTitleOverflowAfterRendering(poster, margin);
}
}

function postersCheckTitleOverflowAfterRendering(poster, margin) {

    const applyTitleOverflowWidth = () => {
        const titleWidth = postersGetPosterTitleWidth(poster);
        if (titleWidth <= 0) {
            return;
        }
        let posterWidth = pagePxSizeToInt(poster.style.width);
        if (pagePxSizeToInt(poster.style.width) < titleWidth) {
            poster.style.width =
                    postersGetPosterResposiveWidth(titleWidth, margin) + "px";
            posterWidth = pagePxSizeToInt(poster.style.width);
            postersApplyExpandedTitleImageAspectRatio(poster, posterWidth);
        }
    };

    browserAfterRendering(() => {
        applyTitleOverflowWidth();
        // Run a second pass to catch delayed layout updates.
        browserAfterRendering(() => applyTitleOverflowWidth());
    });

}

function postersApplyExpandedTitleImageAspectRatio(poster, posterWidth) {
    const imageWrapper = postersGetImageWrapper(poster);
    if (!imageWrapper) {
        return;
    }

    const posterImages = poster.getElementsByClassName("posterImage");
    if (posterImages.length === 0) {
        return;
    }

    const image = posterImages[0];
    if (image.naturalWidth <= 0 || image.naturalHeight <= 0) {
        return;
    }

    const boundedImageWidth = getPostersMaximumImageWidth(
            image.naturalWidth,
            image.naturalHeight
            );

    // If image can still fill wrapper width, keep natural aspect ratio.
    if (posterWidth <= boundedImageWidth) {
        postersApplyPosterImageAspectRatio(
                poster,
                image.naturalWidth,
                image.naturalHeight
                );
        return;
    }

    // Keep wrapper height equal to the bounded image height so only
    // horizontal side margins remain when title expands poster width.
    const boundedImageHeight =
            (boundedImageWidth * image.naturalHeight) / image.naturalWidth;
    if (boundedImageHeight > 0) {
        imageWrapper.style.aspectRatio = posterWidth + " / " + boundedImageHeight;
    }
}

function postersGetPosterResposiveWidth(width, margin) {
    if (typeof margin === "undefined") {
        margin = postersGetMargin();
    }

    if (width < POSTER_MINIMUM_WIDTH) {
        width = POSTER_MINIMUM_WIDTH;
    }

    const browserWidth = browserGetBrowserWidth();
    const totalWidthWithMargin = width + margin;

    if (
            totalWidthWithMargin <= browserWidth &&
            browserWidth > BROWSER_SMALL_SCREEN
            ) {
        return width;
    } else {
        return browserWidth - margin;
    }
}

function postersGetPosterTitleWidth(poster) {
    const posterTitle = poster.getElementsByClassName("posterTitle");
    if (posterTitle.length === 0) {
        return 0;
    }
    return posterTitle[0].offsetWidth;
}

function postersGetYear(date) {
    const parts = date.split("/");
    return parts[2];
}

function postersGetDateString(date, language = i18nGetPageLanguage()) {
    const parts = date.split("/");
    const ddate = new Date(parts[2], parts[1] - 1, parts[0]);
    const options = {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric"
    };
    const dateString = ddate.toLocaleDateString(i18nText(language).locale, options);
    return dateString.charAt(0).toUpperCase() + dateString.slice(1);
}

function postersGetTimeString(time, language = i18nGetPageLanguage()) {
    let dateString = "";
    if (time !== "") {
        dateString = i18nText(language).atTimePrefix + time;
    }
    return dateString;
}

function postersGetEndDateOrTime(beginDate, endDate, time, language = i18nGetPageLanguage()) {
    let dateString = "";
    if (beginDate !== endDate) {
        dateString = "- " + postersGetDateString(endDate, language);
    } else {
        dateString = postersGetTimeString(time, language);
    }
    return dateString;
}

function postersReplaceApostrophesInText(str) {
    return str.split("'").join("&apos;");
}

function postersEscapeSpacesAndApostrophes(str) {
    return str.split(" ").join("-").split("'").join("_");
}

function postersUnescapeSpacesAndApostrophes(str) {
    return str.split("-").join(" ").split("_").join("'");
}

function _postersFetch(createAndAppend) {
    //
    while (_postersToReturn > 0) {
        if (_postersIndex >= _posters.length) {
            if (_postersAllDownloaded === false) {
                _postersFetchFromServer(createAndAppend);
            } else {
                while (_postersToReturn > 0) {
                    const resolve = _postersToResolve.shift();
                    resolve({index: null, poster: null, wrapper: null, image: null});
                    _postersToReturn--;
                }
            }
            break;
        }

        // appending poster to the dom -----------------------------------------
        const currentPoster = _posters[_postersIndex];

        const currentIndex = _postersAbsoluteIndex;
        if (_postersIndexMode === "main") {
            if (
                _postersPosterIndex[currentPoster.id] !== undefined &&
                _postersPosterIndex[currentPoster.id] < currentIndex
            ) {
                _postersIndex++;
                _postersAbsoluteIndex++;
                continue;
            }
        } else {
            if (_postersPosterIndex[currentPoster.id] !== undefined) {
                _postersIndex++;
                _postersAbsoluteIndex++;
                continue;
            }
            if (
                _postersOthersPosterIndex[currentPoster.id] !== undefined &&
                _postersOthersPosterIndex[currentPoster.id] < currentIndex
            ) {
                _postersIndex++;
                _postersAbsoluteIndex++;
                continue;
            }
        }

        if (postersIsExcludedPoster(currentPoster.id)) {
            _postersIndex++;
            _postersAbsoluteIndex++;
            continue;
        }

        if (currentPoster.draft === true) {
            _postersIndex++;
            _postersAbsoluteIndex++;
            continue;
        }

        if (
                (_postersFilter === "" ||
                        currentPoster.labelsList.includes(_postersFilter)) &&
                (_postersFilterTown === "" ||
                        currentPoster.town === _postersFilterTown) &&
                (_postersFilterRegion === "" ||
                        (Array.isArray(_postersFilterRegion) &&
                                _postersFilterRegion.includes(currentPoster.region)) ||
                        currentPoster.region === _postersFilterRegion) &&
                (_postersFilterPublisherId === "" ||
                        currentPoster.publisherId === _postersFilterPublisherId)
                ) {
            // avoiding repetitions due to batches incoherences-----------------
            // resolving promise
            const resolve = _postersToResolve.shift();

            // do not change resolve variable names !!!
            // backward compatibility !!!! TO BE REMOVED WHEN CC & LG & POST UPDATED!!!
            // and map.js too
            let wrapper;
            let image;
            if (createAndAppend !== null) {
                wrapper = createAndAppend(currentPoster, _postersElementWrapper);
                image = wrapper.getElementsByClassName("posterImage")[0];
            }
            const index = _postersAbsoluteIndex;
            const poster = currentPoster;
            resolve({index, poster, wrapper, image});
            if (_postersIndexMode === "main") {
                _postersIndexMap[index] = currentPoster.id;
                if (_postersPosterIndex[currentPoster.id] === undefined ||
                        index < _postersPosterIndex[currentPoster.id]) {
                    _postersPosterIndex[currentPoster.id] = index;
                }
            } else {
                _postersOthersIndexMap[currentIndex] = currentPoster.id;
                if (_postersOthersPosterIndex[currentPoster.id] === undefined ||
                        currentIndex < _postersOthersPosterIndex[currentPoster.id]) {
                    _postersOthersPosterIndex[currentPoster.id] = currentIndex;
                }
            }
            postersSaveIndexMaps();

            //
            _postersToReturn--;
        }
        //
        _postersIndex++;
        _postersAbsoluteIndex++;
    }
}

function posterGetDayMonthPattern(separator) {
    const date = new Date();
    const month = date.getMonth() + 1;
    const day = date.getDate();
    return day + separator + month;
}

function postersSaveCursor(batchNumber, cursor) {
    if (batchNumber !== 0) {
        _postersCursor[batchNumber - 1] = cursor;
    }
}

function postersGetCursor(batchNumber) {
    if (batchNumber === 0) {
        return null;
    }
    return _postersCursor[batchNumber - 1];
}

function posterGetCursorByAbsoluteIndex(absoluteIndex) {
    const auxBatchNumber = Math.floor(absoluteIndex / POSTERS_FETCH_NUMBER);
    return postersGetCursor(auxBatchNumber);
}

// function postersSetCursorCookie(batchNumber, cursor)
// {
//    browserSetCookie("cursor-" + posterGetDayMonthPattern('-') + "-batch-" + batchNumber, cursor, 1);
// }

// function postersGetCursorCookie(batchNumber)
// {
//    return(browserGetCookie("cursor-" + posterGetDayMonthPattern('-') + "-batch-" + batchNumber));
// }

function _postersFetchFromServer(createAndAppend) {
    let _postersQueryLink = "?";
    _postersFetching = true;

    let resource =
            `${CONFIG.posters_api_url}/resource/poster/public/from/${posterGetDayMonthPattern("/")}`;

    if (_postersBatchNumber !== 0) {
        resource += "?cursor=" + postersGetCursor(_postersBatchNumber);
        _postersQueryLink = "&";
    }

    const cacheTime = cacheGetCacheTime();
    if (cacheTime !== null && cacheTime !== "") {
        resource += _postersQueryLink + "cacheTime=" + cacheTime;
    }

    fetch(resource, {headers: {"X-Cartells-Frontend-Client": "EMP_POSTERS"}})
            .then((response) => response.json())
            .then((aux) => {
                //
                _posters.push(...aux.list);
                _postersBatchNumber++;
                postersSaveCursor(_postersBatchNumber, aux.cursor);

                // is there more authors
                if (aux.list.length < POSTERS_FETCH_NUMBER) {
                    _postersAllDownloaded = true;
                }
                _postersFetching = false;
                //
                _postersFetch(createAndAppend);
            })
            .catch((err) => {
                _postersFetching = false;
                _postersReject(err);
            });
}

function postersFetchAPoster(createAndAppend, wrapper) {
    return new Promise(function (resolve, reject) {
        _postersToReturn++;
        _postersElementWrapper = wrapper;
        _postersToResolve.push(resolve);
        // it only saves one !!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!
        _postersReject = reject;

        if (_postersFetching === false) {
            _postersFetch(createAndAppend);
        }
    });
}

function postersIsThereMorePosters() {
    if (_postersAllDownloaded === true) {
        if (_postersIndex >= _posters.length) {
            return false;
        }
    }
    return true;
}

function postersGetPostersIndex() {
    return _postersAbsoluteIndex;
}

function postersSetPostersIndex(index) {
    postersSetPostersIndexAndCursor(index);
}

function postersSetPostersIndexAndCursor(index, cursor = "") {
    // server index
    _postersAbsoluteIndex = index;
    // local array index
    _postersIndex = index % POSTERS_FETCH_NUMBER;
    // offset where to star to fecth in the server
    _postersBatchNumber = Math.floor(index / POSTERS_FETCH_NUMBER);
    // batch cursor
    postersSaveCursor(_postersBatchNumber, cursor);
}

// function posterIsThereCursorForTheIndex(index)
// {
//
//    if (index >= POSTERS_FETCH_NUMBER)
//    {
//        if (postersGetCursorCookie(Math.floor(index / POSTERS_FETCH_NUMBER)) === "")
//        {
//            return(false);
//        }
//    }
//
//    return(true);
// }

function postersSetPostersFilter(filter) {
    _postersFilter = filter;
}

function postersSetExcludedPosterIds(ids) {
    if (Array.isArray(ids) && ids.length > 0) {
        _postersExcludedIds = new Set(ids.filter(Boolean));
    } else {
        _postersExcludedIds = null;
    }
}

function postersIsExcludedPoster(posterId) {
    return _postersExcludedIds !== null && _postersExcludedIds.has(posterId);
}

function postersSetRegionTownFilter(region, town) {
    _postersFilterRegion = region;
    _postersFilterTown = town;
}

function postersSetPublisherFilter(publisherId) {
    _postersFilterPublisherId = publisherId;
}

export {
postersReset,
        postersSetPublisherFilter,
        postersSetRegionTownFilter,
        postersSetPostersFilter,
        postersSetExcludedPosterIds,
        postersSetPostersIndex,
        postersGetPostersIndex,
        postersIsThereMorePosters,
        postersFetchAPoster,
        posterGetCursorByAbsoluteIndex,
        postersUnescapeSpacesAndApostrophes,
        postersGetYear,
        postersSetPosterResposiveWidth,
        getPostersMaximumImageWidth,
        postersResetResolve,
        postersGetEndDateOrTime,
        postersEscapeSpacesAndApostrophes,
        postersReplaceApostrophesInText,
        postersGetDateString,
        postersCheckTitleOverflowAfterRendering,
        postersApplyPosterImageAspectRatio,
        postersGetMargin,
        postersGetPosterResposiveWidth,
        postersSetPostersIndexAndCursor,
        postersSetIndexMode
        };
